<template>
  <div class="container mx-auto">
    <h1 class="text-2xl font-bold mb-6">User Settings</h1>

    <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
      <!-- Main Settings Card -->
      <div class="bg-card rounded-lg shadow p-6">
        <div class="flex items-center mb-8">
          <div class="relative">
            <div class="h-24 w-24 rounded-full bg-primary/20 flex items-center justify-center overflow-hidden">
            <span v-if="!userData?.avatar" class="text-4xl font-semibold text-primary">
              {{ userInitials }}
            </span>
              <img v-else :src="userData.avatar" alt="User avatar" class="h-full w-full object-cover" />
            </div>
            <button
                class="absolute bottom-0 right-0 bg-primary text-primary-foreground rounded-full p-1 shadow-md hover:bg-primary/90 transition-colors"
                title="Change avatar"
            >
              <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            </button>
          </div>
          <div class="ml-6">
            <h2 class="text-xl font-semibold">{{ userData?.name || 'User Name' }}</h2>
            <p class="text-muted-foreground">{{ userData?.email || 'user@example.com' }}</p>
          </div>
        </div>

      <form @submit.prevent="saveSettings" class="space-y-6">
        <div class="grid grid-cols-1 gap-6 md:grid-cols-2">
          <div>
            <label for="name" class="block text-sm font-medium mb-1">Full Name</label>
            <input 
              type="text" 
              id="name" 
              v-model="form.name" 
              class="w-full px-3 py-2 border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
              placeholder="Your full name"
            >
          </div>
          
          <div>
            <label for="email" class="block text-sm font-medium mb-1">Email Address</label>
            <input 
              type="email" 
              id="email" 
              v-model="form.email" 
              class="w-full px-3 py-2 border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
              placeholder="your.email@example.com"
            >
          </div>
          
          <div>
            <label for="username" class="block text-sm font-medium mb-1">Username</label>
            <input 
              type="text" 
              id="username" 
              v-model="form.username" 
              class="w-full px-3 py-2 border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
              placeholder="username"
            >
          </div>
          
          <div>
            <label for="system_role" class="block text-sm font-medium mb-1">System Role</label>
            <input
              type="text"
              id="system_role"
              :value="formatSystemRole(form.system_role as SystemRole)"
              class="w-full px-3 py-2 border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
              placeholder="Your system role"
              disabled
            >
          </div>
          
          <div>
            <label for="business_role" class="block text-sm font-medium mb-1">Business Role</label>
            <select
              id="business_role"
              v-model="form.business_role"
              class="w-full px-3 py-2 border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
              :disabled="form.system_role !== SystemRole.ADMIN"
            >
              <option v-for="role in businessRoles" :key="role" :value="role">{{ formatBusinessRole(role) }}</option>
            </select>
          </div>

          <!-- Report Type Dropdown - Only shown when Business Role is PLANNER -->
          <div v-if="form.business_role === BusinessRole.PLANNER">
            <label for="report_type" class="block text-sm font-medium mb-1">Report Type</label>
            <select
              id="report_type"
              v-model="form.report_type"
              class="w-full px-3 py-2 border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
              :disabled="form.system_role !== SystemRole.ADMIN"
            >
              <option v-for="type in reportTypes" :key="type" :value="type">{{ formatReportType(type) }}</option>
            </select>
          </div>
        </div>
              
        <!-- Success and Error Messages -->
        <div v-if="successMessage || error || passwordError" class="pt-4 border-t border-border">
          <div v-if="successMessage" class="p-3 bg-green-100 text-green-700 rounded-md mb-3">
            {{ successMessage }}
          </div>
          <div v-if="error" class="p-3 bg-red-100 text-red-700 rounded-md mb-3">
            {{ error.message }}
          </div>
          <div v-if="passwordError" class="p-3 bg-red-100 text-red-700 rounded-md mb-3">
            {{ passwordError }}
          </div>
        </div>
        
        <div class="flex justify-end space-x-3">
          <button
            type="button"
            class="px-4 py-2 border border-input rounded-md hover:bg-accent transition-colors"
            @click="resetForm"
          >
            Cancel
          </button>
          <button
            type="submit"
            class="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors"
            :disabled="loading"
          >
            <span v-if="loading">Saving...</span>
            <span v-else>Save Changes</span>
          </button>
        </div>
      </form>
      </div>

      <!-- Right Column with Personal Data and Account Management -->
      <div class="space-y-6">
        <!-- Microsoft Connection Card -->
        <MicrosoftConnectCard
            :userId="userData?.id || (userData?.profile?.id || '')"
            :userEmail="userData?.email || (userData?.profile?.email || '')"
            @microsoft-connected="handleMicrosoftConnected"
            @microsoft-disconnected="handleMicrosoftDisconnected"
        />

        <!-- Personal Data Download Card -->
        <div class="bg-card rounded-lg shadow p-6">
          <h2 class="text-xl font-semibold mb-4">Personal Data</h2>
          <p class="text-muted-foreground mb-4">
            Download a copy of your personal data stored in our system including your profile information and preferences
          </p>
          <button
              @click="downloadPersonalData"
              class="flex items-center px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              :disabled="downloadLoading"
          >
            <svg v-if="!downloadLoading" xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
            </svg>
            <svg v-else class="animate-spin h-5 w-5 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <span v-if="downloadLoading">Downloading...</span>
            <span v-else>Download My Data</span>
          </button>
        </div>

        <!-- Account Management Section -->
        <div class="bg-card rounded-lg shadow p-6">
          <h2 class="text-xl font-semibold mb-4">Account Management</h2>
          <p class="text-muted-foreground mb-4">
            Manage your account settings including account deletion
          </p>
          <button
              @click="showDeleteConfirmation = true"
              class="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors flex items-center"
          >
            <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
            Delete My Account
          </button>
        </div>

        <!-- Delete Account Confirmation Dialog -->
        <div v-if="showDeleteConfirmation" class="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div class="bg-white dark:bg-gray-800 rounded-lg shadow-lg w-full max-w-md p-6">
            <h3 class="text-lg font-medium mb-3 dark:text-white">Confirm Account Deletion</h3>
            <p class="text-gray-600 dark:text-gray-300 mb-6">
              Are you sure you want to delete your account? This action cannot be undone and all your data will be permanently removed.
            </p>

            <div class="flex justify-end space-x-3">
              <button
                  @click="showDeleteConfirmation = false"
                  class="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                Cancel
              </button>
              <button
                  @click="deleteAccount"
                  class="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
                  :disabled="isDeleting"
              >
                <span v-if="isDeleting">Deleting...</span>
                <span v-else>Delete Account</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, watch, PropType } from 'vue';
import { useAuth } from '@/composables/useAuth';
import { useRouter } from 'vue-router';
import { supabase } from '@/services/supabase';
import type { UserSettingsFormData } from '@/types/user';
import type { Profile } from '@/types/supabase';
import { BusinessRole, formatBusinessRole } from '@/types/businessRole';
import { formatSystemRole, SystemRole } from '@/types/systemRole';
import { UserService } from '@/features/user-management/services/userService';
import { REPORT_TYPE } from '@/types/document';
import MicrosoftConnectCard from '@/components/MicrosoftConnectCard.vue';

interface UserData {
  id?: string;
  name: string;
  email: string;
  username: string;
  role: string;
  system_role: SystemRole;
  business_role: BusinessRole;
  avatar: string | null;
  report_type?: string;
  microsoft_connected?: boolean;
  microsoft_email?: string;
}

export default defineComponent({
  name: 'UserSettings',
  components: {
    MicrosoftConnectCard
  },
  props: {
    userData: {
      type: Object as PropType<UserData>,
      required: true
    }
  },
  emits: ['update-user-data'],
  setup(props, { emit }) {
    const { updateProfile, updatePassword, loading, error, logout } = useAuth();
    const downloadLoading = ref(false);
    const showDeleteConfirmation = ref(false);
    const isDeleting = ref(false);
    const router = useRouter();

    const form = ref<UserSettingsFormData>({
      name: '',
      email: '',
      username: '',
      role: 'NOT_USED_ROLE',
      system_role: SystemRole.USER,
      business_role: BusinessRole.PLANNER,
      report_type: REPORT_TYPE.PCO,
      currentPassword: '',
      newPassword: '',
      emailNotifications: true,
      appNotifications: true
    });

    const successMessage = ref<string | null>(null);
    const passwordError = ref<string | null>(null);

    const userInitials = computed(() => {
      if (!props.userData?.name) return '?';
      return props.userData.name
          .split(' ')
          .map(name => name.charAt(0))
          .join('')
          .toUpperCase();
    });

    const businessRoles = Object.values(BusinessRole);
const reportTypes = Object.values(REPORT_TYPE);

    /**
     * Formats a REPORT_TYPE enum value into a human-readable string
     * @param type - The REPORT_TYPE enum value to format
     * @returns A formatted string representation of the report type
     */
    function formatReportType(type: string): string {
      // Convert enum values like "AAC" to "Airport Aviation Center" using the comments in the enum
      switch (type) {
        case REPORT_TYPE.AAC:
          return 'Airport Aviation Center (AAC)';
        case REPORT_TYPE.AAF:
          return 'Airport Administration and Finance (AAF)';
        case REPORT_TYPE.AIR:
          return 'Airport Information and Reporting (AIR)';
        case REPORT_TYPE.AST:
          return 'Airport Security Team (AST)';
        case REPORT_TYPE.CARGO:
          return 'Cargo Operations (CARGO)';
        case REPORT_TYPE.COM:
          return 'Communications (COM)';
        case REPORT_TYPE.HR:
          return 'Human Resources (HR)';
        case REPORT_TYPE.IGS:
          return 'Information and Ground Services (IGS)';
        case REPORT_TYPE.IT:
          return 'Information Technology (IT)';
        case REPORT_TYPE.LEGAL:
          return 'Legal Department (LEGAL)';
        case REPORT_TYPE.MCW:
          return 'Maintenance and Construction Works (MCW)';
        case REPORT_TYPE.PCO:
          return 'Planning and Coordination (PCO)';
        case REPORT_TYPE.PRC:
          return 'Procurement (PRC)';
        case REPORT_TYPE.PUR:
          return 'Purchasing (PUR)';
        case REPORT_TYPE.REM:
          return 'Real Estate Management (REM)';
        default:
          return type;
      }
    }

    function resetForm() {
      const userBusinessRole = props.userData?.business_role;
      const userSystemRole = props.userData?.system_role;
      const userReportType = props.userData?.report_type;

      let businessRole = BusinessRole.PLANNER;

      if (userBusinessRole) {
        // Try to find a matching business role
        const matchingRole = Object.values(BusinessRole).find(role =>
            role.toLowerCase() === userBusinessRole.toLowerCase() ||
            role.replace(/\s+/g, '') === userBusinessRole.replace(/\s+/g, '')
        );

        if (matchingRole) {
          businessRole = matchingRole;
        }
      }

      let systemRole = SystemRole.USER;

      if (userSystemRole) {
        if (userSystemRole === SystemRole.ADMIN || userSystemRole === SystemRole.USER) {
          systemRole = userSystemRole;
        }
      }

      // Default to PCO if no report type is set
      let reportType = REPORT_TYPE.PCO;

      if (userReportType) {
        // Try to find a matching report type
        const matchingType = Object.values(REPORT_TYPE).find(type =>
          type === userReportType
        );

        if (matchingType) {
          reportType = matchingType;
        }
      }

      const formData = {
        name: props.userData?.name || '',
        email: props.userData?.email || '',
        username: props.userData?.username || '',
        system_role: systemRole,
        business_role: businessRole,
        report_type: reportType,
        currentPassword: '',
        newPassword: '',
        emailNotifications: true,
        appNotifications: true,
        role: 'NOT_USED' // Adding this to satisfy the type system
      };

      form.value = formData;
    }

    // Watch for changes in userData and update form
    watch(() => props.userData, resetForm, { immediate: true });

    async function saveSettings() {
      successMessage.value = null;
      passwordError.value = null;

      try {
        // Update profile information
        const profileUpdates: Partial<Profile> = {
          name: form.value.name,
          username: form.value.username,
          role: form.value.system_role === SystemRole.ADMIN ? 'Administrator' : 'User', // Map to database field
          business_role: form.value.business_role
        };

        // Add report_type to profile updates if business role is PLANNER
          if (form.value.business_role === BusinessRole.PLANNER) {
            profileUpdates.report_type = form.value.report_type;
          }

          // Validate business role before saving
        if (!Object.values(BusinessRole).includes(form.value.business_role as BusinessRole)) {
          throw new Error(`Invalid business role: ${form.value.business_role}`);
        }

        // Save profile updates to Supabase
        const updatedProfile = await updateProfile(profileUpdates);

        if (!updatedProfile) {
          throw new Error('Failed to update profile');
        }

        // Update password if provided
        if (form.value.currentPassword && form.value.newPassword) {
          try {
            await updatePassword(form.value.currentPassword, form.value.newPassword);

            // Clear password fields after successful update
            form.value.currentPassword = '';
            form.value.newPassword = '';
          } catch (err) {
            passwordError.value = err instanceof Error ? err.message : 'Failed to update password';
          }
        }

        // Emit event with updated data
        const updatedUserData = {
          ...(props.userData || {}),
          name: form.value.name,
          username: form.value.username,
          system_role: form.value.system_role,
          business_role: form.value.business_role,
            report_type: form.value.business_role === BusinessRole.PLANNER ? form.value.report_type : undefined
        };

        console.log('Emitting updated user data:', updatedUserData);
        emit('update-user-data', updatedUserData);

        successMessage.value = 'Settings saved successfully!';
        
        // Create a simple overlay with spinner
        const overlay = document.createElement('div');
        overlay.className = 'fixed inset-0 bg-white/80 flex items-center justify-center z-50';
        
        // Add spinner using the same SVG pattern that's already in the app
        const spinner = document.createElement('div');
        spinner.innerHTML = `
          <svg class="animate-spin h-10 w-10 text-primary" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
        `;
        overlay.appendChild(spinner);
        
        // Add to body
        document.body.appendChild(overlay);
        
        // Reload the page after a short delay
        setTimeout(() => {
          window.location.reload();
        }, 1000);
      } catch (err) {
        console.error('Profile update error');
        error.value = err instanceof Error ? err : new Error('Failed to update profile');
      }
    }
    /**
     * Download the user's personal data as a JSON file
     */
    async function downloadPersonalData() {
      if (!props.userData) return;

      downloadLoading.value = true;

      try {
        // Fetch the user's profile data
        const { data, error: err } = await supabase
            .from('profiles')
            .select('name, username, avatar_url, email, notification_preferences, business_role, system_role, report_type')
            .eq('email', props.userData.email)
            .eq('username', props.userData.username)
            .single();
        if (err) throw err;

        // Create a JSON blob
        const jsonData = JSON.stringify(data, null, 2);
        const blob = new Blob([jsonData], { type: 'application/json' });

        // Create a download link
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = 'personal-data.json';

        // Trigger the download
        document.body.appendChild(link);
        link.click();

        // Clean up
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      } catch (err) {
        console.error('Error downloading personal data:', err);
        error.value = err instanceof Error ? err : new Error('Failed to download personal data');
      } finally {
        downloadLoading.value = false;
      }
    }

    /**
     * Delete the user's account
     */
    async function deleteAccount() {
      if (!props.userData) {
        console.error('Cannot delete account: No user data available');
        return;
      }

      isDeleting.value = true;

      try {
        console.info('Initiating account deletion process', { userId: props.userData.email });

        // 1. Delete the user's profile from the profiles table
        const { error: profileError } = await supabase
            .from('profiles')
            .delete()
            .eq('email', props.userData.email);

        if (profileError) {
          throw new Error(`Failed to delete profile: ${profileError.message}`);
        }

        console.info('Profile deleted successfully');

        // 2. Try to delete the user from Supabase Auth using the admin API
        try {
          // Create an instance of UserService
          const userService = new UserService();

        // Try to delete the user's auth record
          await userService.deleteUserAuthOnly(props.userData.email);

          console.info('User auth record deleted successfully');
        } catch (authError) {
          console.warn('Could not delete user auth record', { error: authError });
          // Continue with the process even if auth deletion fails
        }

        console.info('User account deletion completed', { userId: props.userData.email });
        // Show success message
        alert('Your account has been completely deleted. You will now be logged out.');

        // Log the user out and redirect to home page
        await logout();

        // Redirect to home page
        router.push('/');
      } catch (err) {
        console.error('Account deletion error', { error: err });
        error.value = err instanceof Error ? err : new Error(String(err));

        // Close the confirmation dialog
        showDeleteConfirmation.value = false;

        // Show error alert
        alert(`Failed to delete account: ${error.value.message}`);
      } finally {
        isDeleting.value = false;
      }
    }

    /**
     * Handle Microsoft connection event
     */
    function handleMicrosoftConnected(data: { microsoftId: string, microsoftEmail: string }) {
      console.log('Microsoft account connected', data);

      // Update local user data
      const updatedUserData = {
        ...props.userData,
        microsoft_connected: true,
        microsoft_email: data.microsoftEmail
      };

      // Emit event to update parent component
      emit('update-user-data', updatedUserData);
    }

    /**
     * Handle Microsoft disconnection event
     */
    function handleMicrosoftDisconnected() {
      console.log('Microsoft account disconnected');

      // Update local user data
      const updatedUserData = {
        ...props.userData,
        microsoft_connected: false,
        microsoft_email: undefined
      };

      // Emit event to update parent component
      emit('update-user-data', updatedUserData);
    }

    return {
      form,
      loading,
      downloadLoading,
      error,
      passwordError,
      successMessage,
      userInitials,
      businessRoles,
      reportTypes,
      resetForm,
      saveSettings,
      downloadPersonalData,
      formatBusinessRole,
      formatSystemRole,
      formatReportType,
      showDeleteConfirmation,
      isDeleting,
      deleteAccount,
      handleMicrosoftConnected,
      handleMicrosoftDisconnected,
      BusinessRole,
      SystemRole
    };
  }
});
</script>

